import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import CheckoutReturnReconciler from './components/common/CheckoutReturnReconciler';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PickupNotificationToast from './components/common/PickupNotificationToast';
import AdminPickupProblemsPanel from './components/admin/AdminPickupProblemsPanel';
import AdminCustomerServiceRequestsPanel from './components/admin/AdminCustomerServiceRequestsPanel';
import AdminCustomerServiceManagementPanel from './components/admin/AdminCustomerServiceManagementPanel';
import AdminMembershipPlansPanel from './components/admin/AdminMembershipPlansPanel';
import AdminDirectChargeRolloutPanel from './components/admin/AdminDirectChargeRolloutPanel';
import AdminDirectChargeFinancePanel from './components/admin/AdminDirectChargeFinancePanel';
import AdminFinanceSection from './components/admin/AdminFinanceSection';
import AdminOrdersTabPanels from './components/admin/AdminOrdersTabPanels';
import MembershipPlanAccessPanel from './components/account/MembershipPlanAccessPanel';
import { SupabaseAuthProvider } from './contexts/SupabaseAuthContext';
import { SupabaseDataProvider } from './contexts/SupabaseDataContext';
import HomePage from './pages/HomePage';
import SellersPage from './pages/SellersPage';
import SellerProfilePage from './pages/SellerProfilePage';
import SellerDashboardPage from './pages/SellerDashboardPage';
import OrdersPage from './pages/OrdersPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminTaxReportPage from './pages/AdminTaxReportPage';
import CustomerServiceDashboardPage from './pages/CustomerServiceDashboardPage';
import NotFoundPage from './pages/NotFoundPage';

const ADMIN_PATH = 'admin-portal';

const AdminRoute = () => (
  <ProtectedRoute requiredRole="admin">
    <AdminDashboardPage />
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminFinanceSection />
      </div>
    </div>
    <AdminOrdersTabPanels />
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminPickupProblemsPanel />
        <AdminCustomerServiceRequestsPanel />
        <AdminCustomerServiceManagementPanel />
        <AdminMembershipPlansPanel />
        <div className="mt-8 space-y-8">
          <AdminDirectChargeRolloutPanel />
          <AdminDirectChargeFinancePanel />
        </div>
      </div>
    </div>
  </ProtectedRoute>
);

const App = () => (
  <BrowserRouter>
    <SupabaseAuthProvider>
      <SupabaseDataProvider>
        <CheckoutReturnReconciler />
        <ScrollToTop />
        <Navbar />
        <PickupNotificationToast />
        <Routes>
          <Route path="/" element={<SellersPage />} />
          <Route path="/about" element={<HomePage />} />
          <Route path="/sellers" element={<SellersPage />} />
          <Route path="/seller/:id" element={<SellerProfilePage />} />
          <Route path="/s/:username" element={<SellerProfilePage />} />
          <Route
            path="/seller-dashboard"
            element={(
              <ProtectedRoute sellerOnly>
                <SellerDashboardPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/orders"
            element={(
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/chat"
            element={(
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/chat/:userId"
            element={(
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/profile"
            element={(
              <ProtectedRoute>
                <>
                  <ProfilePage />
                  <MembershipPlanAccessPanel />
                </>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/customer-service"
            element={(
              <ProtectedRoute>
                <CustomerServiceDashboardPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/customer-service-dashboard"
            element={(
              <ProtectedRoute>
                <CustomerServiceDashboardPage />
              </ProtectedRoute>
            )}
          />
          <Route path={`/${ADMIN_PATH}`} element={<AdminRoute />} />
          {ADMIN_PATH !== 'admin-portal' && (
            <Route path="/admin-portal" element={<AdminRoute />} />
          )}
          <Route
            path="/admin-tax-report"
            element={(
              <ProtectedRoute requiredRole="admin">
                <AdminTaxReportPage />
              </ProtectedRoute>
            )}
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Footer />
      </SupabaseDataProvider>
    </SupabaseAuthProvider>
  </BrowserRouter>
);

export default App;
